import { trackEvent } from "./analytics";

const REGISTRATION_CONTEXT = {
  product: "mental_health",
  platform: "web",
};

let lastViewedStepKey = null;
let hasTrackedRegistrationCompleted = false;

const trackRegistrationEvent = (eventName, params = {}) => {
  trackEvent(eventName, {
    ...REGISTRATION_CONTEXT,
    ...params,
  });
};

export const normalizePaymentType = (paymentType) => {
  if (paymentType === "selfpay" || paymentType === "self_pay") {
    return "self_pay";
  }
  if (paymentType === "insurance") {
    return "insurance";
  }
  return undefined;
};

/**
 * Map registration pathname to analytics step metadata.
 * Welcome (/register) returns null — no registration_step_viewed.
 */
export const getRegistrationStepFromPath = (pathname = "") => {
  const path = String(pathname).split("?")[0].split("#")[0].replace(/\/$/, "");

  if (path === "/register") {
    return null;
  }

  if (
    path === "/register/account" ||
    path === "/register/account/no-rd-available"
  ) {
    return { step_name: "account", step_number: 1 };
  }

  if (path === "/register/account/payment") {
    return { step_name: "payment", step_number: 2 };
  }

  if (path.startsWith("/register/account/insurance")) {
    return { step_name: "insurance", step_number: 3 };
  }

  if (path === "/register/account/card") {
    return { step_name: "card_on_file", step_number: 4 };
  }

  if (
    path === "/register/account/therapists" ||
    /^\/register\/account\/therapists\/[^/]+$/.test(path) ||
    path === "/register/account/no-preference/schedule" ||
    /^\/register\/account\/no-preference\/therapists\/[^/]+$/.test(path)
  ) {
    return { step_name: "provider", step_number: 5 };
  }

  if (
    path === "/register/account/schedule" ||
    path === "/register/account/calendly-booking" ||
    path === "/register/account/confirmation"
  ) {
    return { step_name: "booking", step_number: 6 };
  }

  if (path === "/register/account/app-download") {
    return { step_name: "completed", step_number: 7 };
  }

  return null;
};

export const trackRegistrationStepViewed = (pathname) => {
  const step = getRegistrationStepFromPath(pathname);
  if (!step) {
    return;
  }

  const stepKey = `${step.step_name}:${step.step_number}`;
  if (lastViewedStepKey === stepKey) {
    return;
  }

  lastViewedStepKey = stepKey;
  trackRegistrationEvent("registration_step_viewed", step);
};

export const trackRegistrationStepCompleted = (stepName, stepNumber) => {
  trackRegistrationEvent("registration_step_completed", {
    step_name: stepName,
    step_number: stepNumber,
  });
};

export const trackRegistrationStepBack = (fromStep) => {
  trackRegistrationEvent("registration_step_back", {
    from_step: fromStep,
  });
};

export const trackRegistrationStarted = () => {
  trackRegistrationEvent("registration_started");
};

export const trackRegistrationError = (errorType) => {
  trackRegistrationEvent("registration_error", {
    ...(errorType ? { error_type: errorType } : {}),
  });
};

export const trackRegistrationPaymentTypeSelected = (paymentType) => {
  const normalized = normalizePaymentType(paymentType);
  if (!normalized) {
    return;
  }
  trackRegistrationEvent("registration_payment_type_selected", {
    payment_type: normalized,
  });
};

export const trackRegistrationInsuranceVerified = ({ method, result }) => {
  trackRegistrationEvent("registration_insurance_verified", {
    method,
    result,
  });
};

export const trackRegistrationProviderSelected = (providerId) => {
  trackRegistrationEvent("registration_provider_selected", {
    provider_id: providerId,
  });
};

export const trackRegistrationBookingConfirmed = ({
  paymentType,
  providerId,
}) => {
  const normalized = normalizePaymentType(paymentType);
  trackRegistrationEvent("registration_booking_confirmed", {
    ...(normalized ? { payment_type: normalized } : {}),
    provider_id: providerId,
  });
};

export const trackRegistrationNoProviderFallback = () => {
  trackRegistrationEvent("registration_no_provider_fallback");
};

export const trackProviderAvailabilityChecked = () => {
  trackRegistrationEvent("provider_availability_checked");
};

export const trackBookingStarted = () => {
  trackRegistrationEvent("booking_started");
};

export const trackBookingCompleted = () => {
  trackRegistrationEvent("booking_completed");
};

export const trackRegistrationCompleted = () => {
  if (hasTrackedRegistrationCompleted) {
    return;
  }
  hasTrackedRegistrationCompleted = true;
  trackRegistrationEvent("registration_completed");
};
